import { useCallback, useEffect, useRef, useState } from 'react';

import axios from 'utils/axios';

// Activity events that reset the idle timer. Pointer / keyboard / touch /
// scroll cover both desktop and tablet workflows; `visibilitychange` catches
// the case where the user comes back to the tab.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll', 'wheel', 'visibilitychange'];

// Channel name for cross-tab activity sync. Any activity in one tab also
// resets the idle timer in sibling tabs — otherwise a user reading a chart
// in tab A while typing in tab B would get logged out of A.
const BROADCAST_CHANNEL = 'ayro-idle-activity';

// How often to ping the backend with a heartbeat while the user is active.
// Picked well below the 15-min idle threshold so the server-side
// last_activity_at never drifts stale during a long form-fill.
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;
// Only send a heartbeat when there's been local activity within this window.
// Slightly larger than the interval so we don't miss a tick on which the
// user was active just before the previous heartbeat fired.
const HEARTBEAT_FRESHNESS_MS = 6 * 60 * 1000;

const now = () => Date.now();

/**
 * Idle-timeout hook.
 *
 * @param {Object} opts
 * @param {number} opts.idleMinutes      Total minutes of inactivity before timeout (e.g. 15).
 * @param {number} opts.warningSeconds   Seconds of countdown shown before timeout (e.g. 60).
 * @param {boolean} opts.enabled         When false the hook is a no-op (e.g. on /login, when offline).
 * @param {() => void} opts.onTimeout    Called when the idle window expires.
 *
 * @returns {{
 *   showWarning: boolean,
 *   secondsRemaining: number,
 *   stayActive: () => void,
 * }}
 */
const useIdleTimer = ({ idleMinutes, warningSeconds = 60, enabled = true, onTimeout }) => {
  const idleMs = idleMinutes * 60 * 1000;
  const warningMs = warningSeconds * 1000;

  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(warningSeconds);

  const lastActivityRef = useRef(now());
  const warningTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const channelRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep the latest callback in a ref so the effect below doesn't tear down
  // and re-establish listeners every time the parent re-renders.
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();
    const elapsed = now() - lastActivityRef.current;
    const untilWarning = Math.max(0, idleMs - warningMs - elapsed);
    const untilTimeout = Math.max(0, idleMs - elapsed);

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsRemaining(Math.ceil((idleMs - (now() - lastActivityRef.current)) / 1000));
      countdownIntervalRef.current = setInterval(() => {
        const remainingMs = idleMs - (now() - lastActivityRef.current);
        setSecondsRemaining(Math.max(0, Math.ceil(remainingMs / 1000)));
      }, 1000);
    }, untilWarning);

    timeoutTimerRef.current = setTimeout(() => {
      clearTimers();
      setShowWarning(false);
      if (onTimeoutRef.current) onTimeoutRef.current();
    }, untilTimeout);
  }, [idleMs, warningMs, clearTimers]);

  const resetIdle = useCallback(
    (broadcast = true) => {
      lastActivityRef.current = now();
      setShowWarning(false);
      scheduleTimers();
      if (broadcast && channelRef.current) {
        try {
          channelRef.current.postMessage({ t: lastActivityRef.current });
        } catch {
          // BroadcastChannel can throw on closed channels — ignore.
        }
      }
    },
    [scheduleTimers]
  );

  // "Stay signed in" — manual user action from the warning modal.
  const stayActive = useCallback(() => {
    resetIdle(true);
  }, [resetIdle]);

  useEffect(() => {
    if (!enabled || idleMinutes <= 0) {
      clearTimers();
      setShowWarning(false);
      return undefined;
    }

    // Local activity listeners. Use passive where possible — scroll / touch
    // are the high-frequency ones; this keeps them off the main thread's
    // critical path.
    const handler = () => resetIdle(true);
    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, handler, { passive: true });
    });

    // Cross-tab sync. A peer broadcasting activity resets our timer too,
    // but we do NOT re-broadcast (broadcast=false) to avoid an echo loop.
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(BROADCAST_CHANNEL);
      channelRef.current = channel;
      channel.onmessage = (evt) => {
        if (evt?.data?.t) {
          lastActivityRef.current = evt.data.t;
          setShowWarning(false);
          scheduleTimers();
        }
      };
    }

    // Heartbeat ticker. Keeps the BACKEND's last_activity_at fresh while the
    // user is interacting with a long form (no API calls of its own) — without
    // this, /auth/refresh would refuse the next token rotation and bounce the
    // user to /login mid-task. Only fires when there's been local activity
    // within HEARTBEAT_FRESHNESS_MS, so a truly-idle user does NOT keep the
    // session artificially alive.
    const heartbeatInterval = setInterval(() => {
      if (now() - lastActivityRef.current > HEARTBEAT_FRESHNESS_MS) return;
      axios.post('/api/v1/auth/heartbeat').catch((err) => {
        // Heartbeat failure is best-effort. A 401 here will be picked up by
        // the next real API call's interceptor and routed through the normal
        // refresh path. Anything else (network blip) we just ignore.
        console.warn('[Auth] heartbeat failed:', err?.message);
      });
    }, HEARTBEAT_INTERVAL_MS);

    scheduleTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, handler));
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      clearInterval(heartbeatInterval);
      clearTimers();
    };
  }, [enabled, idleMinutes, resetIdle, scheduleTimers, clearTimers]);

  return { showWarning, secondsRemaining, stayActive };
};

export default useIdleTimer;
